import React from 'react';
import { GraduationCap, Ban, MessageCircleWarning, CalendarCheck } from 'lucide-react';
import { TRAININGS, TRAINING_GROUPS } from '../trainings';
import { formatMoney } from '../../../utils/format';
import { evaluateTrainingReaction } from '../../../data/personalityTraits';
import { getDriverSpecialties, getSpecialtyCostMultiplier } from '../../../utils/driverSpecialties';

/**
 * Ajoute `nbDays` jours-calendaires à la date (day/month/year) du jeu et
 * formate en `JJ/MM/AAAA`. Le moteur HST utilise 30 jours/mois sur 12 mois
 * pour rester pédagogique — on calque le calcul ici.
 *
 * Note : la formation occupe `nbDays` jours d'indispo réelle (cf bug fix
 * 26/06/2026) ; la date de retour est donc J + nbDays.
 */
function formatReturnDate(day, month, year, nbDays) {
  if (day == null || month == null || year == null) return null;
  let d = day + nbDays;
  let m = month;
  let y = year;
  while (d > 30) { d -= 30; m += 1; }
  while (m > 12) { m -= 12; y += 1; }
  const dd = String(d).padStart(2, '0');
  const mm = String(m).padStart(2, '0');
  return `${dd}/${mm}/${y}`;
}

/**
 * TrainingPanel — catalog of trainings purchasable for a driver, grouped
 * by license / sensibilisation / specialty. Handles personality refusals,
 * already-acquired licenses, cost-multipliers, and disabled states.
 *
 * Extracted from `parts.jsx` on 31/05/2026 to keep parts.jsx light.
 * Visual output and data-testids preserved verbatim.
 */
export function TrainingPanel({ driver, treasury, onTrain, setShowTraining, t, calDay, calMonth, calYear }) {
  return (
    <div className="mt-3 border border-accent/40 rounded-lg p-3 bg-accent/5 space-y-3" data-testid={`training-panel-${driver.id}`}>
      <p className="text-xs font-semibold text-blue-800 flex items-center gap-1">
        <GraduationCap className="w-3.5 h-3.5" /> {t('available_trainings')}
      </p>
      <div className="rounded-md bg-ink-700/30 border border-ink-600 px-2.5 py-1.5 text-[10px] text-slate-400 leading-relaxed" data-testid={`training-help-${driver.id}`}>
        <strong className="text-slate-300">Bon à savoir :</strong>{' '}
        Seules les <strong className="text-accent">spécialisations</strong> qualifient pour les contrats dédiés (Frigo, BTP, ADR…).
        Les <em>sensibilisations</em> et l&apos;éco-conduite boostent la compétence sans débloquer de contrat.
        Les permis et spécialités déjà acquis sont marqués
        <span className="mx-1 px-1 py-0.5 rounded bg-money/10 text-money font-mono">✓ Déjà acquis</span> et grisés.
      </div>
      {/* 17/06/2026 — Avertissement explicite si le chauffeur est en mission
          (cas Thibaut Renard, chauffeur racheté assigné à un contrat hérité).
          Sans cet avertissement, l'utilisateur cliquait sur "Former" sans
          comprendre pourquoi rien ne se passait. */}
      {driver.assignedVehicleId && (driver.status === 'active' || driver.onMission) && (
        <div
          className="rounded-md bg-amber-500/10 border border-amber-500/40 px-2.5 py-1.5 text-[11px] text-amber-700 dark:text-amber-300 leading-relaxed"
          data-testid={`training-blocked-on-mission-${driver.id}`}
        >
          <strong>{(driver.name || 'Ce chauffeur')} est actuellement en mission.</strong>{' '}
          Toute formation est bloquée jusqu&apos;à la fin du contrat actuel. Détachez-le du
          véhicule (« Désaffecter ») pour pouvoir le former immédiatement.
        </div>
      )}
      {TRAINING_GROUPS.map(group => {
        const items = TRAININGS.filter(it => it.group === group.id);
        if (items.length === 0) return null;
        return (
          <div key={group.id} className="space-y-1.5" data-testid={`training-group-${driver.id}-${group.id}`}>
            <div className="border-b border-ink-600 pb-1 mb-1">
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-300 font-mono">{group.label}</p>
              <p className="text-[10px] text-slate-500 italic mt-0.5">{group.subtitle}</p>
            </div>
            {items.map(tr => (
              <TrainingRow key={tr.id} tr={tr} driver={driver} treasury={treasury} onTrain={onTrain} setShowTraining={setShowTraining} t={t} calDay={calDay} calMonth={calMonth} calYear={calYear} />
            ))}
          </div>
        );
      })}
    </div>
  );
}

function TrainingRow({ tr, driver, treasury, onTrain, setShowTraining, t, calDay, calMonth, calYear }) {
  const alreadyHas = tr.id === 'permisC' && (driver.licenses || []).includes('C');
  const alreadyHasCE = tr.id === 'permisCE' && (driver.licenses || []).includes('CE');
  // 05/02/2026 — Q1 : habilitation convoi exceptionnel (permisExceptional).
  const alreadyHasExc = tr.id === 'permisExceptional'
    && ((driver.licenses || []).includes('CE+exceptionnel') || driver.license === 'CE+exceptionnel');
  // Le permisExceptional exige le CE en prérequis.
  const missingCE = tr.id === 'permisExceptional'
    && !((driver.licenses || []).includes('CE') || driver.license === 'CE');
  const alreadyDone = (driver.trainings || []).some(dt => dt.type === tr.id);
  const driverSpecs = getDriverSpecialties(driver);
  const specBlocked = !!tr.spec && driverSpecs.includes(tr.spec);
  const reaction = evaluateTrainingReaction(driver, tr.id);
  const personalityRefused = !!reaction.refused;
  const costMultiplier = tr.spec ? getSpecialtyCostMultiplier(driverSpecs.length) : 1;
  const scaledCost = Math.round(tr.cost * costMultiplier);
  // 17/06/2026 — Bug user : Thibaut Renard (chauffeur racheté) ne pouvait
  // pas être formé. Cause : il était lié à un contrat hérité au moment
  // du rachat (status='active', assignedVehicleId set), et trainDriver
  // retournait silencieusement { ok: false, reason: 'on_mission' }.
  // On expose désormais ce blocage côté UI (bouton désactivé + raison).
  const onMission = driver.assignedVehicleId && (driver.status === 'active' || driver.onMission);
  const busy = driver.status === 'training';
  const disabled = treasury < scaledCost || alreadyHas || alreadyHasCE || alreadyHasExc || missingCE || specBlocked
    || personalityRefused
    || onMission
    || busy
    || (alreadyDone && !['ecoConduite', 'gestionStress'].includes(tr.id));
  const specReason = specBlocked ? 'Déjà acquise' : null;
  const refuseReason = personalityRefused ? `Refuse (${reaction.label})` : null;
  const missionReason = onMission ? 'En mission' : null;
  const busyReason = busy ? 'En formation' : null;
  const prereqReason = missingCE ? 'Permis CE requis' : null;
  const buttonLabel = prereqReason || specReason || refuseReason || missionReason || busyReason
    || (alreadyHas || alreadyHasCE || alreadyHasExc ? t('acquired') : alreadyDone ? t('done') : formatMoney(scaledCost));
  return (
    <div className={`flex items-center justify-between rounded-md p-2 border ${
      personalityRefused ? 'bg-danger/5 border-danger/40' :
      reaction.malus ? 'bg-warn/5 border-warn/40' : 'bg-ink-800 border-blue-100'
    }`} data-testid={`training-row-${driver.id}-${tr.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-slate-200 flex items-center gap-1">
          {tr.name}
          {tr.spec && costMultiplier > 1 && (
            <span className="text-[10px] px-1 py-0.5 rounded bg-warn/15 text-warn font-mono font-bold"
              title={`Slot ${driverSpecs.length + 1} — coût ×${costMultiplier.toFixed(1)}`}>
              ×{costMultiplier.toFixed(1)}
            </span>
          )}
        </p>
        <p className="text-[10px] text-slate-500">{tr.description} — {tr.days}{t('training_days')}</p>
        {/* 26/06/2026 — date de retour estimée (recommandation user
            validée). N'affiche que si la formation est bloquante (medical
            est non-blocking → on cache la ligne pour éviter le bruit) et
            si on a un calendrier en scope. */}
        {tr.id !== 'medical' && calDay != null && calMonth != null && calYear != null && (
          <p
            className="text-[10px] text-slate-400 flex items-center gap-1 mt-0.5"
            data-testid={`return-date-${driver.id}-${tr.id}`}
          >
            <CalendarCheck className="w-2.5 h-2.5 text-accent/70" />
            Retour prévu : <span className="font-mono text-slate-300">{formatReturnDate(calDay, calMonth, calYear, tr.days)}</span>
          </p>
        )}
        {personalityRefused && (
          <p className="text-[10px] text-danger mt-0.5 flex items-center gap-0.5" data-testid={`refuse-warn-${driver.id}-${tr.id}`}>
            <Ban className="w-2.5 h-2.5" /> Refusera ({reaction.label})
          </p>
        )}
        {reaction.malus && !personalityRefused && (
          <p className="text-[10px] text-warn mt-0.5 flex items-center gap-0.5" data-testid={`malus-warn-${driver.id}-${tr.id}`}>
            <MessageCircleWarning className="w-2.5 h-2.5" /> Maugréera (-8 satisfaction · {reaction.label})
          </p>
        )}
      </div>
      <button onClick={() => { onTrain(driver.id, tr.id); setShowTraining(false); }} disabled={disabled}
        className={`text-[10px] px-2.5 py-1 rounded font-medium disabled:opacity-40 transition-colors flex-shrink-0 text-white ${
          personalityRefused ? 'bg-danger/100' : reaction.malus ? 'bg-warn/100' : 'bg-blue-600 hover:bg-blue-700'
        }`} data-testid={`train-${driver.id}-${tr.id}`}>
        {buttonLabel}
      </button>
    </div>
  );
}
